import { FieldConfiguration, FieldEntry, ParentFieldEntry, GetAvailableFieldsResult } from '@aidkitorg/types/lib/admin-panel/settings';
import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { DataTableRow } from '../AdminPanel/Settings/types';
import { usePost } from '../API';
import { logAdminPanelError } from '../utils/adminPanel.utils';

const SYNTHETIC_UID_COLUMN_NAME = "__synthetic_uid__";

type SettingsProviderProps = {
  children: React.ReactNode
}

type SettingsContextType = {
  fields: FieldEntry[];
  transformedFields: DataTableRow[];
  getFields: () => void;
  saveField: (id: string, updates: Partial<FieldEntry>) => Promise<void>;
  isFieldsLoading: boolean;
};

const SettingsContext = createContext<SettingsContextType>({
  fields: [],
  transformedFields: [],
  getFields: async () => {},
  saveField: async () => {},
  isFieldsLoading: false,
});

export const useSettings = () => {
  const context = useContext(SettingsContext)
  if (!context) {
    throw new Error("useSettings must be used within a SettingsProvider")
  }

  return context;
}

// Transform fields for DataTable display putting parent fields and their children together
function transformFieldsForDataTable(fields: FieldEntry[]): DataTableRow[] {
  // First pass: build parent-child relationships and identify all field IDs
  const parentToChildren = new Map<string, string[]>();
  const allFieldIds = new Set<string>();
  const childFields = new Set<string>();

  fields.forEach(field => {
    allFieldIds.add(field.id);

    if (field.parent) {
      // This field has a parent, so it's a child
      childFields.add(field.id);

      if (!parentToChildren.has(field.parent)) {
        parentToChildren.set(field.parent, []);
      }
      parentToChildren.get(field.parent)!.push(field.id);
    }
  });

  // Second pass: identify true root fields (fields that are not children of any other field)
  const rootFields = Array.from(allFieldIds).filter(fieldId => !childFields.has(fieldId));

  // Convert to DataTable format
  return rootFields.map(rootField => {
    // Handle synthetic UID column as a special case
    const targetField = rootField === SYNTHETIC_UID_COLUMN_NAME ? 'uid' : rootField;
    
    const fieldEntry = fields.find(f => f.id === rootField);
    const parentFieldEntry = fieldEntry && !fieldEntry.parent ? fieldEntry as ParentFieldEntry : undefined;

    return {
      targetField: targetField,
      relatedFields: parentToChildren.get(rootField) || [],
      kind: fieldEntry?.kind,
      category: parentFieldEntry?.category,
      aiCategory: parentFieldEntry?.aiCategory,
      autoCategorized: parentFieldEntry?.autoCategorized,
      displayName: parentFieldEntry?.displayName,
      description: parentFieldEntry?.description
    };
  }).sort((a, b) => a.targetField.localeCompare(b.targetField));
}

export const SettingsProvider: React.FC<SettingsProviderProps> = ({ children }) => {
  const [fields, setFields] = useState<FieldEntry[]>([]);
  const [isFieldsLoading, setIsFieldsLoading] = useState<boolean>(false);
  const [hasFetched, setHasFetched] = useState<boolean>(false);

  const getAvailableFields = usePost("/api/admin_panel/settings/availableFields", { suppressErrorDisplay: true })
  const updateFieldConfigurations = usePost("/api/admin_panel/settings/updateFieldConfigurations", { suppressErrorDisplay: true })

  const fetchFieldsInternal = useCallback(async () => {
    // Prevent multiple simultaneous API calls
    if (isFieldsLoading) {
      return;
    }
    
    setIsFieldsLoading(true);
    const result = await getAvailableFields({ structuredOutput: true })
    setIsFieldsLoading(false);
    setHasFetched(true);

    if (result.success && result.data) {
      const fieldsArray = (result.data as GetAvailableFieldsResult).fields;
      if (Array.isArray(fieldsArray) && fieldsArray.length > 0) {
        if (typeof fieldsArray[0] === 'string') {
          // Simple string array - convert to FieldEntry
          setFields(fieldsArray.map(field => ({ id: field } as FieldEntry)));
        } else {
          // Already FieldEntry objects with hierarchical structure
          setFields(fieldsArray as FieldEntry[]);
        }
      }
    } else if (result.error) {
      logAdminPanelError(result.error, result.message)
    }
  }, [getAvailableFields])

  // Auto-fetch fields once when the provider mounts
  React.useEffect(() => {
    if (!hasFetched && !isFieldsLoading) {
      fetchFieldsInternal();
    }
  }, [hasFetched, isFieldsLoading, fetchFieldsInternal]);

  // Public API for forcing a refresh (e.g., after updates)
  const getFields = useCallback(async () => {
    await fetchFieldsInternal();
  }, [fetchFieldsInternal])


  const saveField = useCallback(async (id: string, updates: Partial<FieldEntry>) => {
    // optimistic update while preserving order
    setFields(prevFields => prevFields.map(f => f.id === id ? { ...f, ...updates } as FieldEntry : f));

    const config: FieldConfiguration = { id, ...updates }
    const res = await updateFieldConfigurations({ configurations: [config] })
    if (!res.success) {
      // rollback on error
      const rollbackResult = await getAvailableFields({ structuredOutput: true })
      if (rollbackResult.success && rollbackResult.data) {
        const fieldsArray = (rollbackResult.data as GetAvailableFieldsResult).fields;
        if (Array.isArray(fieldsArray)) {
          if (fieldsArray.length > 0 && typeof fieldsArray[0] === 'string') {
            setFields(fieldsArray.map(field => ({ id: field } as FieldEntry)));
          } else {
            setFields(fieldsArray as FieldEntry[]);
          }
        }
      }
      // Throw error so InlineEditableText can show error state
      throw new Error(res.message || 'Failed to save field configuration');
    }
  }, [updateFieldConfigurations, getAvailableFields])

  const transformedFields = useMemo(() => {
    return transformFieldsForDataTable(fields);
  }, [fields])

  const value = useMemo(() => ({
    fields,
    transformedFields,
    getFields,
    saveField,
    isFieldsLoading
  }), [fields, transformedFields, getFields, saveField, isFieldsLoading]);
  
  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  )
}